import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { concatMap, Observable, tap } from 'rxjs';

import { environment } from '../../../../environments/environment';

import { ImageService } from '../../global-store';

import { CompanyDetailsModel, UpdateCompanyDetailsRequestModel } from '../../model';

import RequestCache from '../../utils/RequestCache';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  public readonly companyDetails: RequestCache<CompanyDetailsModel>;

  constructor(
    private http: HttpClient,
    private imageService: ImageService,
  ) {
    this.companyDetails = new RequestCache(
      this.http
        .get<CompanyDetailsModel>(`${environment.apiEndpoint}/account/company`)
        .pipe(this.imageService.loadImages('companyLogoUrl')),
      'companyDetails',
    );
  }

  public updateCompanyDetails(body: UpdateCompanyDetailsRequestModel): Observable<void> {
    return this.http.put<void>(`${environment.apiEndpoint}/account/company`, body).pipe(
      tap((): void => {
        this.companyDetails.clearCache();
      }),
    );
  }

  public getReferralInvitation(): Observable<{ showReferralInvitation: boolean }> {
    return this.http.get<{ showReferralInvitation: boolean }>(
      `${environment.apiEndpoint}/account/company/referral-invitation`,
    );
  }

  public updateReferralInvitation(body: { showReferralInvitation: boolean }): Observable<void> {
    return this.http.put<void>(`${environment.apiEndpoint}/account/company/referral-invitation`, body);
  }

  public uploadCompanyLogo(file: File): Observable<HttpResponse<string>> {
    this.companyDetails.updateValue((current) => ({
      ...current,
      companyLogoUrl: URL.createObjectURL(file),
    }));

    return this.http.put(`${environment.apiEndpoint}/account/company/logo`, null, { responseType: 'text' }).pipe(
      concatMap((uploadUrl: string) =>
        this.http.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type,
            'x-amz-acl': 'private',
            'Content-Disposition': `attachment; filename="${file.name}"`,
          },
          observe: 'response',
          responseType: 'text',
        }),
      ),
    );
  }

  public deleteCompanyLogo(): Observable<void> {
    this.companyDetails.updateValue((current: CompanyDetailsModel) => ({ ...current, companyLogoUrl: null }));

    return this.http.delete<void>(`${environment.apiEndpoint}/account/company/logo`);
  }
}
